import { inject, Injectable } from '@angular/core';

import { WindowService } from 'src/shared/providers/window.service';
import { PosthogService } from 'src/shared/services/posthog.service';

import { FEATURE_FLAGS, FEATURE_FLAG_DEFAULTS } from 'src/shared/constants/feature-flags';

import { environment } from 'src/environments/environment';

/**
 * PostHog "pm-schedule-call-first" experiment: re-orders the demo conversion funnel. Variant
 * `schedule-call-first` nudges the demo visitor to schedule a call BEFORE creating an account —
 * the demo indicator reads "Schedule Call" and opens the Get Started flow at the schedule entry
 * (step 2), then (after they book, tracked client-side by the `pm_scf_scheduled` localStorage
 * flag) flips to "Create Account" and opens at step 1. `control` keeps the current
 * "Get Started" → create-account-first flow.
 *
 * Exposure ($feature_flag_called) is recorded at demo login (recordExposure(), from
 * DemoAccountService.loginToDemoAccount) — same entry point as the other PM-demo experiments.
 * KPI = scheduling a call (the `scheduled_call` event, shared with the account-manager and
 * schedule-call-selection experiments; PostHog attributes per-experiment via exposure). Mirrors
 * PmScheduleCallSelectionService: anonymous per-device bucketing, sticky cache, no-exposure read;
 * prod vs sandbox by getFlagKey(); flags carry NO targeting.
 */
@Injectable({ providedIn: 'root' })
export class PmScheduleCallFirstService {

  private posthogService = inject(PosthogService);
  private windowService = inject(WindowService);

  private getFlagKey(): string {
    return environment.environment === 'production'
      ? FEATURE_FLAGS.PM_SCHEDULE_CALL_FIRST
      : FEATURE_FLAGS.PM_SCHEDULE_CALL_FIRST_SANDBOX;
  }

  /** The currently bucketed variant (from sticky cache), for branching the indicator / flow. */
  getVariant(): string {
    const flagKey = this.getFlagKey();
    return localStorage.getItem(`posthog_variant_${flagKey}`)
      || (FEATURE_FLAG_DEFAULTS[flagKey] as string);
  }

  /** Warm the variant cache early (no exposure) so the indicator/flow can branch instantly. */
  prefetchVariant(): void {
    this.resolveVariant().catch(() => { /* warm-up only */ });
  }

  /** Record experiment exposure ($feature_flag_called) — call at demo login. */
  async recordExposure(): Promise<void> {
    const variant = await this.resolveVariant();
    this.posthogService.capture('$feature_flag_called', {
      $feature_flag: this.getFlagKey(),
      $feature_flag_response: variant,
    });
  }

  async resolveVariant(): Promise<string> {
    const flagKey = this.getFlagKey();
    const cacheKey = `posthog_variant_${flagKey}`;

    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      return cached;
    }

    await this.posthogService.waitForIdentification();
    await this.posthogService.waitForFlags();
    const raw = await this.posthogService.getFeatureFlagNoExposure(flagKey);
    const variant = (raw as string) || (FEATURE_FLAG_DEFAULTS[flagKey] as string);

    if (raw !== null && raw !== undefined && raw !== false) {
      localStorage.setItem(cacheKey, variant);
    }

    return variant;
  }

  /** Capture a funnel event tagged with the resolved variant and the platform form factor. */
  capture(event: string, properties: Record<string, any> = {}): void {
    this.posthogService.capture(event, {
      variant: this.getVariant(),
      is_desktop: this.windowService.isDesktopRes,
      ...properties,
    });
  }
}
